/**
 * Bounded Context Pattern - Domain Entity
 * 
 * Represents the meta-model of a bounded context pattern including its structure,
 * dependencies, and compliance with architectural standards.
 * 
 * Following Eric Evans DDD principles for meta-modeling domain architecture.
 */

import { Entity, EntityId } from '@swoft/core';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';

// Value Objects
export const BoundedContextPatternIdSchema = z.string().uuid();

export class BoundedContextPatternId extends EntityId {
  constructor(private readonly value: string) {
    super();
  }

  static generate(): BoundedContextPatternId {
    return new BoundedContextPatternId(uuidv4());
  }

  static fromString(value: string): BoundedContextPatternId {
    return new BoundedContextPatternId(value);
  }

  toString(): string {
    return this.value;
  }
}

export const BoundedContextTypeSchema = z.enum(['core', 'supporting', 'generic']);
export type BoundedContextType = z.infer<typeof BoundedContextTypeSchema>;

export const ModulePatternTypeSchema = z.enum(['createXModule', 'static-configure', 'no-module']);
export type ModulePatternType = z.infer<typeof ModulePatternTypeSchema>;

export const ArchitecturalPatternSchema = z.object({
  hasCleanArchitecture: z.boolean(),
  hasPortsAndAdapters: z.boolean(),
  hasCQRS: z.boolean(),
  hasEventSourcing: z.boolean(),
  hasDomainEvents: z.boolean(),
  hasAntiCorruptionLayer: z.boolean(),
});
export type ArchitecturalPattern = z.infer<typeof ArchitecturalPatternSchema>;

export const DependencyAnalysisSchema = z.object({
  inboundDependencies: z.array(z.string()),
  outboundDependencies: z.array(z.string()),
  circularDependencies: z.array(z.string()),
  violatesCleanArchitecture: z.boolean(),
});
export type DependencyAnalysis = z.infer<typeof DependencyAnalysisSchema>;

export const ComplianceScoreSchema = z.object({
  overall: z.number().min(0).max(100),
  modulePattern: z.number().min(0).max(100),
  symbolUsage: z.number().min(0).max(100),
  architecturalCompliance: z.number().min(0).max(100),
  dependencyHealth: z.number().min(0).max(100),
});
export type ComplianceScore = z.infer<typeof ComplianceScoreSchema>;

// Main Entity Schema
export const BoundedContextPatternSchema = z.object({
  id: BoundedContextPatternIdSchema,
  packageName: z.string(),
  contextName: z.string(),
  contextType: BoundedContextTypeSchema,
  modulePatternType: ModulePatternTypeSchema,
  hasSymbolConstants: z.boolean(),
  symbolConstantName: z.string().optional(),
  architecturalPatterns: ArchitecturalPatternSchema,
  dependencyAnalysis: DependencyAnalysisSchema,
  complianceScore: ComplianceScoreSchema,
  lastAnalyzedAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type BoundedContextPatternData = z.infer<typeof BoundedContextPatternSchema>;

/**
 * Bounded Context Pattern Entity
 * 
 * Aggregate root for meta-model analysis of bounded contexts.
 * Provides insights into architectural compliance and pattern consistency.
 */
export class BoundedContextPattern extends Entity<BoundedContextPatternId> {
  private constructor(
    id: BoundedContextPatternId,
    private readonly _packageName: string,
    private readonly _contextName: string,
    private readonly _contextType: BoundedContextType,
    private _modulePatternType: ModulePatternType,
    private _hasSymbolConstants: boolean,
    private _symbolConstantName: string | undefined,
    private _architecturalPatterns: ArchitecturalPattern,
    private _dependencyAnalysis: DependencyAnalysis,
    private _complianceScore: ComplianceScore,
    private _lastAnalyzedAt: Date,
    private readonly _createdAt: Date,
    private _updatedAt: Date
  ) {
    super(id);
  }

  // Factory Methods
  static create(data: Omit<BoundedContextPatternData, 'id' | 'createdAt' | 'updatedAt'>): BoundedContextPattern {
    const id = BoundedContextPatternId.generate();
    const now = new Date();
    
    return new BoundedContextPattern(
      id,
      data.packageName,
      data.contextName,
      data.contextType,
      data.modulePatternType,
      data.hasSymbolConstants,
      data.symbolConstantName,
      data.architecturalPatterns,
      data.dependencyAnalysis,
      data.complianceScore,
      data.lastAnalyzedAt,
      now,
      now
    );
  }

  static fromData(data: BoundedContextPatternData): BoundedContextPattern {
    return new BoundedContextPattern(
      BoundedContextPatternId.fromString(data.id),
      data.packageName,
      data.contextName,
      data.contextType,
      data.modulePatternType,
      data.hasSymbolConstants,
      data.symbolConstantName,
      data.architecturalPatterns,
      data.dependencyAnalysis,
      data.complianceScore,
      data.lastAnalyzedAt,
      data.createdAt,
      data.updatedAt
    );
  }

  // Getters
  get packageName(): string { return this._packageName; }
  get contextName(): string { return this._contextName; }
  get contextType(): BoundedContextType { return this._contextType; }
  get modulePatternType(): ModulePatternType { return this._modulePatternType; }
  get hasSymbolConstants(): boolean { return this._hasSymbolConstants; }
  get symbolConstantName(): string | undefined { return this._symbolConstantName; }
  get architecturalPatterns(): ArchitecturalPattern { return this._architecturalPatterns; }
  get dependencyAnalysis(): DependencyAnalysis { return this._dependencyAnalysis; }
  get complianceScore(): ComplianceScore { return this._complianceScore; }
  get lastAnalyzedAt(): Date { return this._lastAnalyzedAt; }
  get createdAt(): Date { return this._createdAt; }
  get updatedAt(): Date { return this._updatedAt; }

  // Business Logic Methods
  
  /**
   * Update analysis results
   */
  updateAnalysis(
    modulePatternType: ModulePatternType,
    hasSymbolConstants: boolean,
    symbolConstantName: string | undefined,
    architecturalPatterns: ArchitecturalPattern,
    dependencyAnalysis: DependencyAnalysis
  ): void {
    this._modulePatternType = modulePatternType;
    this._hasSymbolConstants = hasSymbolConstants;
    this._symbolConstantName = symbolConstantName;
    this._architecturalPatterns = architecturalPatterns;
    this._dependencyAnalysis = dependencyAnalysis;
    this._complianceScore = this.calculateComplianceScore();
    this._lastAnalyzedAt = new Date();
    this._updatedAt = new Date();
  }

  /**
   * Calculate compliance score based on patterns and architecture
   */
  private calculateComplianceScore(): ComplianceScore {
    // Module Pattern Score (25%)
    const modulePatternScore = this._modulePatternType === 'createXModule' ? 100 :
                              this._modulePatternType === 'static-configure' ? 50 : 0;

    // Symbol Usage Score (25%)
    const symbolUsageScore = this._hasSymbolConstants ? 100 : 0;

    // Architectural Compliance Score (25%)
    const archPatterns = this._architecturalPatterns;
    const archScore = [
      archPatterns.hasCleanArchitecture,
      archPatterns.hasPortsAndAdapters,
      archPatterns.hasCQRS,
      archPatterns.hasAntiCorruptionLayer
    ].filter(Boolean).length * 25;

    // Dependency Health Score (25%)
    const depAnalysis = this._dependencyAnalysis;
    const dependencyScore = depAnalysis.violatesCleanArchitecture ? 0 :
                           depAnalysis.circularDependencies.length > 0 ? 50 : 100;

    // Overall Score
    const overallScore = Math.round(
      (modulePatternScore + symbolUsageScore + archScore + dependencyScore) / 4
    );

    return {
      overall: overallScore,
      modulePattern: modulePatternScore,
      symbolUsage: symbolUsageScore,
      architecturalCompliance: archScore,
      dependencyHealth: dependencyScore,
    };
  }

  /**
   * Check if context follows recommended patterns
   */
  isCompliant(): boolean {
    return this._complianceScore.overall >= 80;
  }

  /**
   * Get recommendations for improvement
   */
  getRecommendations(): string[] {
    const recommendations: string[] = [];

    if (this._modulePatternType !== 'createXModule') {
      recommendations.push('Implement createXModule() pattern for consistency');
    }

    if (!this._hasSymbolConstants) {
      recommendations.push('Add X_SYMBOLS constant for type-safe dependency injection');
    }

    if (!this._architecturalPatterns.hasCleanArchitecture) {
      recommendations.push('Implement Clean Architecture with proper layer separation');
    }

    if (!this._architecturalPatterns.hasPortsAndAdapters) {
      recommendations.push('Add ports and adapters for external dependencies');
    }

    if (this._dependencyAnalysis.violatesCleanArchitecture) {
      recommendations.push('Fix dependency direction violations');
    }

    if (this._dependencyAnalysis.circularDependencies.length > 0) {
      recommendations.push('Resolve circular dependencies: ' + this._dependencyAnalysis.circularDependencies.join(', '));
    }

    return recommendations;
  }

  /**
   * Convert to plain data object
   */
  toData(): BoundedContextPatternData {
    return {
      id: this.id.toString(),
      packageName: this._packageName,
      contextName: this._contextName,
      contextType: this._contextType,
      modulePatternType: this._modulePatternType,
      hasSymbolConstants: this._hasSymbolConstants,
      symbolConstantName: this._symbolConstantName,
      architecturalPatterns: this._architecturalPatterns,
      dependencyAnalysis: this._dependencyAnalysis,
      complianceScore: this._complianceScore,
      lastAnalyzedAt: this._lastAnalyzedAt,
      createdAt: this._createdAt,
      updatedAt: this._updatedAt,
    };
  }
}